import { serviceRequestPriorityChoices, serviceRequestStatusChoices, serviceRequestTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { formatTimePassed } from '@/helpers/date/formatTimePassed.js';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { defaultDateFieldOptions } from '@/constants/Date.jsx';

const joinParts = parts => parts.filter(Boolean).join(' | ');

const formatCollectionPointValue = record => {
    const collectionPoint = record?.collectionPoint;
    const collectionPointAddressParts = collectionPoint?.address
        ? [collectionPoint.address.address, collectionPoint.address.postalCode ? `${collectionPoint.address.postalCode} ${collectionPoint.address.city}` : '']
        : [];
    const collectionPointAddress = collectionPointAddressParts.filter(Boolean).join(', ');
    const collectionPointPhones = [collectionPoint?.contact?.contactPhoneNumber, collectionPoint?.contact?.employeePhoneNumber].filter(Boolean).join(', ');
    return joinParts([collectionPoint?.customId, collectionPoint?.contractor?.name, collectionPointAddress, collectionPointPhones]);
};

const formatStatusLabel = (record, translate) => {
    if (record?.status === 'new') {
        return translate('resources.serviceRequest.labels.statusNew');
    }

    return serviceRequestStatusChoices(translate).find(choice => choice.id === record?.status)?.name ?? translate('resources.device.fields.notSpecified');
};

const formatStatusValue = (record, translate) => {
    const statusLabel = formatStatusLabel(record, translate);
    const statusTime = record?.updatedAt
        ? record?.status === 'new'
            ? formatIntervalSince(record.updatedAt)
            : formatTimePassed(record.updatedAt, translate)
        : '';
    return joinParts([statusLabel, statusTime]);
};

const formatDeviceStateValue = (record, translate, deviceStates) => {
    const deviceStateLabel = deviceStates.find(state => state.id === record?.device?.state)?.name ?? '';
    let deviceUptime = '';
    if (record?.device?.uptime !== null && record?.device?.uptime !== undefined) {
        deviceUptime = `${record.device.uptime}% ${translate('common.uptime')}`;
    }
    let deviceTransactions = '';
    if (record?.device?.transactions !== null && record?.device?.transactions !== undefined) {
        deviceTransactions = `${record.device.transactions} ${translate('common.transactionsCount')}`;
    }
    return joinParts([deviceStateLabel, deviceUptime, deviceTransactions]);
};

const formatErrorValue = (record, translate) => {
    const error = record?.error;
    if (!error || (error.level != null && error.level > 3)) {
        return translate('common.shortage');
    }

    const errorTime = error?.time ? new Date(error.time).toLocaleString('pl-PL', defaultDateFieldOptions) : '';
    return joinParts([`Error: ${error.code ?? ''}`, error.title ?? error.name ?? '', errorTime]);
};

const formatUsersValue = (record, translate) => {
    const coordinatorName = record?.coordinatorId ? `${record.coordinator?.firstName ?? ''} ${record.coordinator?.lastName ?? ''}`.trim() : '';
    const servicemanName = record?.servicemanId ? `${record.serviceman?.firstName ?? ''} ${record.serviceman?.lastName ?? ''}`.trim() : '';
    return [
        `${translate('resources.serviceRequest.labels.coordinator')}: ${coordinatorName || translate('resources.device.fields.notSpecified')}`,
        `${translate('resources.serviceRequest.labels.serviceman')}: ${servicemanName || translate('resources.device.fields.notSpecified')}`,
    ].join(' | ');
};

const getSourceLabel = record => {
    if (record?.sourceType === 'auto') {
        return 'Automatycznie';
    }
    if (record?.sourceType === 'collection_point') {
        return 'CollectionPoint';
    }
    if (record?.sourceType === 'user' && record?.source) {
        return `${record.source.firstName ?? ''} ${record.source.lastName ?? ''}`.trim();
    }
    return '';
};

const formatSourceValue = record => {
    const createdAt = record?.createdAt ? new Date(record.createdAt).toLocaleString('pl-PL', defaultDateFieldOptions) : '';
    return joinParts([getSourceLabel(record), createdAt]);
};

export const mapServiceRequestExportRow = (record, translate, deviceStates) => {
    let formattedId = '';
    if (record?.customId !== null && record?.customId !== undefined) {
        formattedId = `#${record.customId.toString().padStart(6, '0')}`;
    }
    const priorityLabel = serviceRequestPriorityChoices(translate).find(choice => choice.id === record?.priority)?.name ?? '';
    const typeLabel = serviceRequestTypeChoices(translate).find(choice => choice.id === record?.type)?.name ?? '';
    const commentCount = record?.changeCount ?? record?.visibleChangeCount ?? 0;
    const requestValue = joinParts([formattedId, priorityLabel, typeLabel, `${commentCount}`]);
    const collectionPointValue = formatCollectionPointValue(record);
    const statusValue = formatStatusValue(record, translate);
    const deviceStateValue = formatDeviceStateValue(record, translate, deviceStates);
    const errorValue = formatErrorValue(record, translate);
    const usersValue = formatUsersValue(record, translate);
    const sourceWithDate = formatSourceValue(record);

    return {
        [translate('resources.serviceRequest.labels.requestTitle')]: requestValue,
        [translate('common.collectionPoint')]: collectionPointValue,
        [translate('resources.device.fields.serialNumber')]: record?.device?.serialNumber ?? '',
        [translate('common.status')]: statusValue,
        [translate('resources.serviceRequest.labels.deviceStateTitle')]: deviceStateValue,
        [translate('resources.serviceRequest.labels.causeTitle')]: errorValue,
        [translate('resources.serviceRequest.labels.coordinator') + ' / ' + translate('resources.serviceRequest.labels.serviceman')]: usersValue,
        [translate('resources.serviceRequest.labels.sourceTitle')]: sourceWithDate,
    };
};
